import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCrmMeta } from "../../hooks/useCrmMeta";
import { Phone, Mail, Video, StickyNote, X, Plus, FileText, Link2 } from "lucide-react";

const TYPE_ICON = {
    Call: Phone,
    Email: Mail,
    Meeting: Video,
    Note: StickyNote,
};

const RELATED_TYPES = [
    { value: "lead", label: "Lead", endpoint: "/crm/leads", labelFields: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || r.id },
    { value: "deal", label: "Deal", endpoint: "/crm/deals", labelFields: (r) => r.deal_name || r.record_no || r.id },
    { value: "account", label: "Account", endpoint: "/crm/accounts", labelFields: (r) => r.account_name || r.id },
    { value: "contact", label: "Contact", endpoint: "/crm/contacts", labelFields: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.id },
];

function emptyForm(defaultType) {
    return {
        type: defaultType || "Call",
        direction: "Outbound",
        related_type: "",
        related_id: "",
        related_label: "",
        occurred_at: toLocalInputValue(new Date()),
        duration_minutes: "",
        outcome: "",
        subject: "",
        pitch: "",
        presented_quotation_ids: [],
        presented_quotation_nos: [],
        presented_items: [],
        presented_url: "",
        presented_notes: "",
        followUp: false,
        due_date: "",
        priority: "Medium",
    };
}

function toLocalInputValue(date) {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

// Renders a related record's label safely — never dumps a raw object into JSX.
function safeLabel(value, fallback = "—") {
    if (value == null) return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);
    return fallback;
}

export default function ActivityLogModal({ open, onClose, onSaved, defaultType, related }) {
    const { meta } = useCrmMeta();
    const [form, setForm] = useState(() => emptyForm(defaultType));
    const [saving, setSaving] = useState(false);

    // related-record search (only used when `related` prop isn't preset)
    const [relQuery, setRelQuery] = useState("");
    const [relResults, setRelResults] = useState([]);
    const [relSearching, setRelSearching] = useState(false);

    // presented quotation search
    const [quoteQuery, setQuoteQuery] = useState("");
    const [quoteOptions, setQuoteOptions] = useState([]);
    const [quotesLoaded, setQuotesLoaded] = useState(false);

    // presented item chip inputs
    const [itemCode, setItemCode] = useState("");
    const [itemName, setItemName] = useState("");

    useEffect(() => {
        if (open) {
            setForm({
                ...emptyForm(defaultType),
                related_type: related?.type || "",
                related_id: related?.id || "",
                related_label: safeLabel(related?.label, ""),
            });
            setRelQuery("");
            setRelResults([]);
            setQuoteQuery("");
            setItemCode("");
            setItemName("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultType, related?.type, related?.id, related?.label]);

    const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const relatedTypeMeta = useMemo(
        () => RELATED_TYPES.find((r) => r.value === form.related_type),
        [form.related_type]
    );

    const runRelatedSearch = async (q) => {
        if (!relatedTypeMeta) return;
        setRelSearching(true);
        try {
            const { data } = await api.get(relatedTypeMeta.endpoint, { params: { q } });
            setRelResults(Array.isArray(data) ? data.slice(0, 20) : []);
        } catch (e) {
            setRelResults([]);
        } finally {
            setRelSearching(false);
        }
    };

    const pickRelated = (record) => {
        update("related_id", record.id);
        update("related_label", safeLabel(relatedTypeMeta?.labelFields(record), record.id));
        setRelResults([]);
        setRelQuery("");
    };

    const loadQuotesIfNeeded = async () => {
        if (quotesLoaded) return;
        try {
            const { data } = await api.get("/quotations");
            setQuoteOptions(Array.isArray(data) ? data : []);
        } catch (e) {
            setQuoteOptions([]);
        } finally {
            setQuotesLoaded(true);
        }
    };

    const filteredQuotes = useMemo(() => {
        const q = quoteQuery.trim().toLowerCase();
        const already = new Set(form.presented_quotation_ids);
        const pool = quoteOptions.filter((qt) => !already.has(qt.id));
        if (!q) return pool.slice(0, 8);
        return pool
            .filter(
                (qt) =>
                    String(qt.doc_no || "").toLowerCase().includes(q) ||
                    String(qt.customer_name || "").toLowerCase().includes(q)
            )
            .slice(0, 8);
    }, [quoteQuery, quoteOptions, form.presented_quotation_ids]);

    const addQuotation = (q) => {
        update("presented_quotation_ids", [...form.presented_quotation_ids, q.id]);
        update("presented_quotation_nos", [...form.presented_quotation_nos, q.doc_no || q.id]);
        setQuoteQuery("");
    };

    const removeQuotation = (idx) => {
        update("presented_quotation_ids", form.presented_quotation_ids.filter((_, i) => i !== idx));
        update("presented_quotation_nos", form.presented_quotation_nos.filter((_, i) => i !== idx));
    };

    const addItem = () => {
        const code = itemCode.trim();
        if (!code) {
            toast.error("Item code is required to add a presented item.");
            return;
        }
        update("presented_items", [...form.presented_items, { item_code: code, item_name: itemName.trim() }]);
        setItemCode("");
        setItemName("");
    };

    const removeItem = (idx) => {
        update("presented_items", form.presented_items.filter((_, i) => i !== idx));
    };

    const close = () => {
        if (saving) return;
        onClose && onClose();
    };

    const submit = async () => {
        const subject = form.subject.trim();
        if (!subject) {
            toast.error("Subject is required.");
            return;
        }
        if (!related && !form.related_type) {
            toast.error("Choose what this activity relates to.");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                type: form.type,
                subject,
                related_type: related?.type || form.related_type,
                related_id: related?.id || form.related_id || null,
                related_label: related?.label ? safeLabel(related.label) : (form.related_label || null),
                occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : new Date().toISOString(),
                direction: form.direction,
                outcome: form.outcome || null,
                duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
                pitch: form.pitch || null,
                presented_quotation_ids: form.presented_quotation_ids,
                presented_quotation_nos: form.presented_quotation_nos,
                presented_items: form.presented_items,
                presented_url: form.presented_url || null,
                presented_notes: form.presented_notes || null,
            };

            if (form.followUp && form.due_date) {
                payload.due_date = new Date(form.due_date).toISOString();
                payload.priority = form.priority;
                payload.completed = false;
            }

            await api.post("/crm/activities", payload);
            toast.success(`${form.type} logged`);
            onSaved && (await onSaved());
            onClose && onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const showDuration = form.type === "Call" || form.type === "Meeting";
    const showOutcome = form.type === "Call";

    return (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={close}>
            <div
                className="sc-card p-5 w-full max-w-2xl space-y-4 bg-background max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="activity-log-modal"
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Log Activity</div>
                        <div className="text-lg font-semibold">Record a Call, Email, Meeting or Note</div>
                    </div>
                    <button onClick={close} className="p-1 rounded hover:bg-secondary text-muted-foreground">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Type segmented control */}
                <div>
                    <div className="text-xs font-medium mb-1">Type</div>
                    <div className="grid grid-cols-4 gap-2">
                        {(meta.logged_types || ["Call", "Email", "Meeting", "Note"]).map((t) => {
                            const Icon = TYPE_ICON[t] || StickyNote;
                            const active = form.type === t;
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => update("type", t)}
                                    className={`h-10 inline-flex items-center justify-center gap-1.5 rounded-md border text-sm ${
                                        active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                                    }`}
                                    data-testid={`activity-type-${t.toLowerCase()}`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {t}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Direction */}
                <div>
                    <div className="text-xs font-medium mb-1">Direction</div>
                    <div className="grid grid-cols-2 gap-2">
                        {(meta.activity_directions || ["Outbound", "Inbound"]).map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => update("direction", d)}
                                className={`h-9 rounded-md border text-sm ${
                                    form.direction === d ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                                }`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Related record */}
                <div>
                    <div className="text-xs font-medium mb-1">Related record</div>
                    {related ? (
                        <div className="sc-input flex items-center h-9 bg-secondary/40 text-sm capitalize">
                            {related.type}: {safeLabel(related.label, related.id)}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="grid grid-cols-4 gap-2">
                                {RELATED_TYPES.map((rt) => (
                                    <button
                                        key={rt.value}
                                        type="button"
                                        onClick={() => {
                                            update("related_type", rt.value);
                                            update("related_id", "");
                                            update("related_label", "");
                                            setRelResults([]);
                                            setRelQuery("");
                                        }}
                                        className={`h-9 rounded-md border text-xs ${
                                            form.related_type === rt.value ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                                        }`}
                                    >
                                        {rt.label}
                                    </button>
                                ))}
                            </div>

                            {form.related_type && (
                                <div className="space-y-2">
                                    {form.related_id ? (
                                        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                                            <span>{form.related_label || form.related_id}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    update("related_id", "");
                                                    update("related_label", "");
                                                }}
                                                className="text-muted-foreground hover:text-red-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    className="sc-input"
                                                    placeholder={`Search ${relatedTypeMeta?.label || "records"}…`}
                                                    value={relQuery}
                                                    onChange={(e) => {
                                                        setRelQuery(e.target.value);
                                                        runRelatedSearch(e.target.value);
                                                    }}
                                                    onFocus={() => runRelatedSearch(relQuery)}
                                                />
                                                <input
                                                    className="sc-input"
                                                    placeholder="Or type a free-text label"
                                                    value={form.related_label}
                                                    onChange={(e) => update("related_label", e.target.value)}
                                                />
                                            </div>
                                            {relSearching && <div className="text-xs text-muted-foreground mt-1">Searching…</div>}
                                            {relResults.length > 0 && (
                                                <div className="mt-1 border border-border rounded-md max-h-40 overflow-y-auto bg-background shadow-sm">
                                                    {relResults.map((r) => (
                                                        <button
                                                            key={r.id}
                                                            type="button"
                                                            onClick={() => pickRelated(r)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                                                        >
                                                            {safeLabel(relatedTypeMeta?.labelFields(r), r.id)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Occurred at">
                        <input
                            type="datetime-local"
                            className="sc-input"
                            value={form.occurred_at}
                            onChange={(e) => update("occurred_at", e.target.value)}
                        />
                    </Field>

                    {showDuration && (
                        <Field label="Duration (minutes)">
                            <input
                                type="number"
                                min="0"
                                className="sc-input"
                                value={form.duration_minutes}
                                onChange={(e) => update("duration_minutes", e.target.value)}
                            />
                        </Field>
                    )}

                    <Field label="Outcome">
                        {showOutcome ? (
                            <select className="sc-input" value={form.outcome} onChange={(e) => update("outcome", e.target.value)}>
                                <option value="">Select outcome…</option>
                                {(meta.call_outcomes || []).map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                className="sc-input"
                                placeholder="Optional outcome"
                                value={form.outcome}
                                onChange={(e) => update("outcome", e.target.value)}
                            />
                        )}
                    </Field>
                </div>

                <Field label="Subject" required>
                    <input
                        className="sc-input"
                        placeholder="e.g. Follow-up on Q4 tile order"
                        value={form.subject}
                        onChange={(e) => update("subject", e.target.value)}
                        data-testid="activity-subject"
                    />
                </Field>

                <Field label="Pitch — what did you say / present?">
                    <textarea
                        className="sc-input h-auto py-2"
                        rows={4}
                        placeholder="Summarize the pitch, objections raised, next steps discussed…"
                        value={form.pitch}
                        onChange={(e) => update("pitch", e.target.value)}
                        data-testid="activity-pitch"
                    />
                </Field>

                {/* Presented section */}
                <div className="rounded-md border border-border p-3 space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Presented to customer</div>

                    <div>
                        <div className="text-xs font-medium mb-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Quotation(s)</div>
                        <div className="relative">
                            <input
                                className="sc-input"
                                placeholder="Search doc no / customer…"
                                value={quoteQuery}
                                onFocus={loadQuotesIfNeeded}
                                onChange={(e) => setQuoteQuery(e.target.value)}
                            />
                            {quoteQuery && filteredQuotes.length > 0 && (
                                <div className="mt-1 border border-border rounded-md max-h-40 overflow-y-auto bg-background shadow-sm">
                                    {filteredQuotes.map((q) => (
                                        <button
                                            key={q.id}
                                            type="button"
                                            onClick={() => addQuotation(q)}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between gap-2"
                                        >
                                            <span className="font-mono text-xs">{q.doc_no}</span>
                                            <span className="text-muted-foreground text-xs truncate">{safeLabel(q.customer_name)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {form.presented_quotation_nos.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {form.presented_quotation_nos.map((no, i) => (
                                    <span key={`${no}-${i}`} className="sc-pill bg-secondary text-foreground border border-border font-mono">
                                        {safeLabel(no)}
                                        <button type="button" onClick={() => removeQuotation(i)} className="ml-1">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-xs font-medium mb-1">Item(s) presented</div>
                        <div className="flex items-center gap-2">
                            <input
                                className="sc-input"
                                placeholder="Item code"
                                value={itemCode}
                                onChange={(e) => setItemCode(e.target.value)}
                            />
                            <input
                                className="sc-input"
                                placeholder="Item name (optional)"
                                value={itemName}
                                onChange={(e) => setItemName(e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={addItem}
                                className="h-9 px-3 shrink-0 rounded-md border border-border text-sm hover:bg-secondary inline-flex items-center gap-1"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add
                            </button>
                        </div>
                        {form.presented_items.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {form.presented_items.map((it, i) => (
                                    <span key={`${it.item_code}-${i}`} className="sc-pill bg-secondary text-foreground border border-border">
                                        <span className="font-mono">{safeLabel(it.item_code)}</span>
                                        {it.item_name ? ` · ${safeLabel(it.item_name)}` : ""}
                                        <button type="button" onClick={() => removeItem(i)} className="ml-1">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <Field label="Deck / presentation link">
                        <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                            <input
                                className="sc-input"
                                placeholder="https://…"
                                value={form.presented_url}
                                onChange={(e) => update("presented_url", e.target.value)}
                            />
                        </div>
                    </Field>

                    <Field label="Presented notes">
                        <textarea
                            className="sc-input h-auto py-2"
                            rows={2}
                            placeholder="Anything else shown or shared during the interaction"
                            value={form.presented_notes}
                            onChange={(e) => update("presented_notes", e.target.value)}
                        />
                    </Field>
                </div>

                {/* Follow-up task */}
                <div className="rounded-md border border-border p-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.followUp}
                            onChange={(e) => update("followUp", e.target.checked)}
                        />
                        Create a follow-up task
                    </label>

                    {form.followUp && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <Field label="Due date">
                                <input
                                    type="date"
                                    className="sc-input"
                                    value={form.due_date}
                                    onChange={(e) => update("due_date", e.target.value)}
                                />
                            </Field>
                            <Field label="Priority">
                                <select className="sc-input" value={form.priority} onChange={(e) => update("priority", e.target.value)}>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </Field>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <button onClick={close} className="h-9 px-3 rounded border border-border text-sm" disabled={saving}>
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50 inline-flex items-center gap-2"
                        data-testid="activity-save"
                    >
                        {saving ? "Saving…" : "Save Activity"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required = false, children }) {
    return (
        <label className="space-y-1 block">
            <div className="text-xs font-medium">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </div>
            {children}
        </label>
    );
}
